import { FolderKanban, MoreHorizontal, PlusCircle, Server } from "lucide-react";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { cn, fetcherTyped, formatDistanceTime, HttpError, okstatus } from "@/lib/utils";
import useSWR from "swr";
import chance from "chance";
import React from "react";

import { AppShell } from "@/components/AppShell";
import { Host } from "@/types";
import { menu } from "@/components/dashboard/Sidebar";
import { ObjectLink } from "@/components/OptionalLink";
import { DeleteHostDialog } from "@/components/host/DeleteHostDialog";
import logger from "@/lib/logger";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { CopyValue } from "@/components/CopyValue";
import { ListHostsResponseSchema } from "@/types/schemas";
import { groupByProject } from "@/pages/app";
import { formatMarkdown } from "@/components/formatMarkdown";
import { FeatureHeader } from "@/components/dashboard/FeatureHeader";

const Page = () => {
  const router = useRouter();
  const tab = (router.query.tab as string) ?? "all";
  const {
    data: hosts,
    mutate,
    isValidating
  } = useSWR("/api/host?status=" + tab, fetcherTyped(ListHostsResponseSchema));
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAdd = useCallback(async () => {
    try {
      setLoading(true);
      await fetch("/api/host/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: chance().word({ length: 12 })
        })
      })
        .then(okstatus)
        .then(res => res.json() as Promise<Host>)
        .then(res => {
          router.push("/host/" + res.id);
        });
    } catch (err) {
      logger.warn("Failed to create host", err);
      if (err instanceof HttpError) {
        if (err.statusCode === 402) {
          setShowSubscribeDialog(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [router]);

  const { projects, groups } = useMemo(() => groupByProject(hosts), [hosts]);

  return (
    <AppShell menuItem={menu.host}>
      {showSubscribeDialog && (
        <SubscribeDialog onClose={() => setShowSubscribeDialog(false)} />
      )}
      <div
        className={cn(
          "mx-4 mt-4 mb-20 flex flex-col gap-4",
          hosts && hosts.length === 0 ? "items-center justify-center lg:m-16" : ""
        )}
      >
        <FeatureHeader
          key="hosts"
          icon={<Server />}
          title="Servers"
          subtitle="Connect your own server or provision a new one to host your apps and services."
          open={hosts && hosts.length === 0}
          collapsible={hosts && hosts.length > 0}
          actions={[
            { label: "Add a new server", onClick: handleAdd, variant: "default" },
            {
              label: "Documentation",
              href: "https://docs.dollardeploy.com/getting-started",
              variant: "secondary"
            }
          ]}
          checklist={[
            { label: "Link your DigitalOcean, Hetzner or Verda Cloud account" },
            {
              label: "Provision a new server",
              done: hosts && hosts.some(h => h.status === "active")
            },
            { label: "Add managed services like Postgres or Redis" }
          ]}
        >
          {formatMarkdown(`
Bring your existing server or create a new one through integrated cloud providers.
Each server can run multiple apps and managed services like Postgres, Redis, and more.
`)}
        </FeatureHeader>

        {hosts && hosts.length > 0 && (
          <Tabs value={tab} onValueChange={value => router.push("/host?tab=" + value)}>
            <div className="flex items-center justify-between">
              <TabsList className="hidden md:flex">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="archived" className="hidden sm:flex">
                  Archived
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-8 gap-1 plausible-event-name=AddHost"
                  onClick={handleAdd}
                  disabled={loading}
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Add Host
                </Button>
                <Button size="sm" className="h-8 gap-1" onClick={() => router.push("/r")}>
                  <PlusCircle className="mr-2 h-3.5 w-3.5 gap-1" /> Create host + app{" "}
                  <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                    from template
                  </span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1"
                  onClick={e => {
                    e.preventDefault();
                    mutate();
                  }}
                >
                  Refresh
                </Button>
              </div>
            </div>

            <TabsContent value={tab}>
              {hosts && hosts?.length > 0 ? (
                <Card>
                  <CardContent>
                    <Table className="Responsive">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="md:pl-4">Name</TableHead>
                          <TableHead>IP address</TableHead>
                          <TableHead>Apps</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody className={cn(isValidating && "opacity-50")}>
                        {Object.entries(groups).map(([projectId, hosts]) => (
                          <React.Fragment key={projectId}>
                            {projectId !== "0" || Object.keys(projects).length > 1 ? (
                              <TableRow className="transparent hover:transparent">
                                <TableCell colSpan={6}>
                                  <div className="flex items-center gap-2 font-semibold text-lg py-2">
                                    <FolderKanban className="w-5 h-5" />
                                    {projects[projectId]?.name || "No project assigned"}
                                  </div>

                                  {projects[projectId]?.description ? (
                                    <div className="prose dark:prose-invert prose-sm line-clamp-2">
                                      {formatMarkdown(projects[projectId]?.description)}
                                    </div>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            ) : null}

                            {hosts.map(host => (
                              <TableRow
                                key={host.id}
                                className="cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => router.push(`/host/${host.id}`)}
                              >
                                <TableCell className="pl-4 font-medium">
                                  <ObjectLink
                                    href={`/host/${host.id}`}
                                    className="link"
                                    type="host"
                                  >
                                    {host.name}
                                  </ObjectLink>
                                </TableCell>
                                <TableCell data-label="IP address">
                                  <CopyValue value={host.ipAddress || undefined} />
                                </TableCell>
                                <TableCell data-label="Apps">
                                  <span className="inline-flex gap-1 flex-wrap">
                                    {host.apps.map((app, index) => (
                                      <ObjectLink
                                        key={app.id}
                                        href={`/app/${app.id}`}
                                        className={cn(
                                          "link",
                                          index > 0 && "hidden md:inline-flex"
                                        )}
                                        type="app"
                                      >
                                        {app.name}
                                      </ObjectLink>
                                    ))}

                                    {host.apps.length > 1 && (
                                      <span className="text-muted-foreground inline-block md:hidden">
                                        + {host.apps.length - 1} apps
                                      </span>
                                    )}

                                    {host.apps.length === 0 && (
                                      <span className="text-muted-foreground">No apps</span>
                                    )}
                                  </span>
                                </TableCell>
                                <TableCell data-label="Status">
                                  <Badge>{host.status ?? "Draft"}</Badge>
                                </TableCell>
                                <TableCell data-label="Created">
                                  {formatDistanceTime(host.createdAt)}
                                </TableCell>
                                <TableCell
                                  className="items-center gap-2 flex"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <Button variant="outline" size="sm" asChild>
                                    <Link href={`/host/${host.id}`}>View</Link>
                                  </Button>

                                  <DropdownMenu modal={false}>
                                    <DropdownMenuTrigger asChild>
                                      <Button aria-haspopup="true" size="icon" variant="ghost">
                                        <MoreHorizontal className="h-4 w-4" />
                                        <span className="sr-only">Toggle menu</span>
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                      <DropdownMenuItem asChild>
                                        <Link href={`/host/${host.id}`}>View</Link>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem asChild>
                                        <Link href={`/host/${host.id}/edit`}>Edit</Link>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem asChild>
                                        <Link href={`/journal?hostId=${host.id}`}>Logs</Link>
                                      </DropdownMenuItem>
                                      <DeleteHostDialog
                                        id={host.id}
                                        onDelete={() => mutate()}
                                      />
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                  <CardFooter className="px-4">
                    <div className="text-xs text-muted-foreground">
                      Showing{" "}
                      <strong>
                        {hosts && hosts?.length > 0 ? 1 : 0}-{hosts?.length}
                      </strong>{" "}
                      of <strong>{hosts?.length}</strong> hosts
                    </div>
                  </CardFooter>
                </Card>
              ) : (
                <Card className="p-12 md:p-24">
                  <CardContent className="flex flex-col justify-center  gap-4 text-center">
                    Add your exising server or create a new one with our integrated host
                    providers.
                  </CardContent>
                  <CardFooter className="flex justify-center">
                    <Button onClick={() => handleAdd()} variant="outline">
                      Add Host
                    </Button>
                  </CardFooter>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppShell>
  );
};

export default Page;
